import React, { AriaAttributes } from 'react';
import classnames from 'classnames';
import type { PapiDeviation } from '@wix/da-papi-types';
import type { NullableDropdownItem } from '@wix/da-ds/pkg/Dropdown/types';
import { useDeviationMatureFilter } from '../../../../utils/hooks/useDeviationMatureFilter';
import { getIsMatureBlock } from '../../../../Moderation/redux/selectors';
import { DeviationTypes } from '../../../../types/deviation';
import { getDeviationType } from '../../../_helpers';
import DeviationThumbOverlayDefault from '../DeviationThumbOverlayDefault';
import DeviationThumbOverlayTier from '../DeviationThumbOverlayTier';
import DeviationThumbMiddleContent from '../DeviationThumbMiddleContent';
import ThumbOverlayBackground from '../ThumbOverlayBackground';
import ThumbOverlayHover from '../ThumbOverlayHover';

import s from './Overlay.scss';

export interface Props {
  deviation: PapiDeviation;
  width?: number;
  height?: number;
  className?: string;
  isFaved?: boolean;
  hasCommentedOn?: boolean;
  extraActions?: NullableDropdownItem[];
  children?: React.ReactNode;
  'aria-hidden'?: AriaAttributes['aria-hidden'];
  onRemove?: () => void;
}

const Overlay: React.FC<React.PropsWithChildren<Props>> = ({
  deviation,
  width,
  height,
  className,
  hasCommentedOn,
  children,
  isFaved,
  'aria-hidden': ariaHidden,
  extraActions,
  onRemove,
}) => {
  const { displayReason: blockReason } = useDeviationMatureFilter(deviation);
  const isMatureBlock = getIsMatureBlock(blockReason);
  const type = getDeviationType(deviation);
  const DeviationThumbOverlay =
    type === DeviationTypes.TIER ? DeviationThumbOverlayTier : DeviationThumbOverlayDefault;

  return (
    <div
      className={classnames(s['root'], className)}
      style={{ width, height }}
      aria-hidden={ariaHidden}
    >
      <ThumbOverlayBackground className={s['underlay']} deviation={deviation} showDefaultHover />
      <div className={s['cover']}>
        <DeviationThumbOverlay
          deviation={deviation}
          width={width}
          height={height}
          isFaved={isFaved}
          hasCommentedOn={hasCommentedOn}
        />
      </div>
      <ThumbOverlayHover
        className={s['cover']}
        deviation={deviation}
        width={width}
        height={height}
        extraActions={extraActions}
        onRemove={onRemove}
      />
      <DeviationThumbMiddleContent
        className={classnames(s['middle'], isMatureBlock && s['mature-block-middle'])}
        deviation={deviation}
        thumbWidth={width}
        thumbHeight={height}
        isMatureBlock={isMatureBlock}
      >
        {children}
      </DeviationThumbMiddleContent>
    </div>
  );
};
Overlay.displayName = 'Overlay';

export default Overlay;
